import { useCallback, useEffect, useState, MouseEvent } from 'react';
import { api, useLazyGetUsersListQuery, useLazyPostCaseRegisterQuery } from '@/api';
import {
  selectCaseIdData,
  setCaseIdData,
  setCaseMetadata,
  setCasesFinding,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { ProgressSpinner, TablePagination } from '@repo/atomic-components';
import { CaseTemplate } from '@/ui/templates';
import { MermaiddMovedBanner } from '@/ui/components/MermaiddMovedBanner';
import { ActionsPanel, CaseListTable } from './components';
import { CreateEditCaseSidebar } from '../components';
import { useCasesListTable } from './hooks';
import { useNavigate } from 'react-router';
import { AppRoutes } from '@/constants';
import { openNewTab } from '@/utils';

export const CaseListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isCreateEditCaseSidebarVisible, setIsCreateEditCaseSidebarVisible] = useState<boolean>(false);

  const caseId = useAppSelector(selectCaseIdData);
  const [triggerGetUsersList, { isLoading: isGetUsersListLoading }] = useLazyGetUsersListQuery();
  const [triggerPostCaseRegister, { isSuccess: isPostRegisterCaseSuccess }] = useLazyPostCaseRegisterQuery();

  const handleCloseCaseSidebarClick = useCallback(() => {
    setIsCreateEditCaseSidebarVisible(false);
  }, []);

  const { table, filterOptions, selectedFilterOptions, handleFilterChange, isTableDataLoading } = useCasesListTable({
    setIsCreateEditCaseSidebarVisible: setIsCreateEditCaseSidebarVisible,
  });

  useEffect(() => {
    return () => {
      dispatch(setCaseIdData(null));
      dispatch(setCaseMetadata(null));
      // Reset the search term so a stale query from this session doesn't
      // silently filter the list when the user comes back to the page.
      dispatch(setCasesFinding(''));
      dispatch(api.util.resetApiState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    triggerGetUsersList({}, true);
  }, [triggerGetUsersList]);

  useEffect(() => {
    if (isPostRegisterCaseSuccess && caseId) {
      setIsCreateEditCaseSidebarVisible(true);
    }
  }, [caseId, isPostRegisterCaseSuccess, setIsCreateEditCaseSidebarVisible]);

  const handleCreateNewCase = useCallback(() => {
    dispatch(setCaseMetadata(null));
    triggerPostCaseRegister({}, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerPostCaseRegister]);

  const handleTableRowClick = useCallback(
    (event: MouseEvent<HTMLTableRowElement>, caseId: string) => {
      if ((event.metaKey || event.ctrlKey) && caseId) {
        const newUrl = `/${AppRoutes.CaseList}/${caseId}`;
        openNewTab(newUrl);
        return;
      }

      if (caseId) {
        dispatch(setCaseIdData(caseId));
        navigate(`/${AppRoutes.CaseList}/${caseId}`);
      }
    },
    [dispatch, navigate],
  );

  return (
    <CaseTemplate
      isDataLoading={isGetUsersListLoading}
      actionsPanel={<ActionsPanel handleCreateNewCase={handleCreateNewCase} />}
      banner={<MermaiddMovedBanner />}
    >
      <div className="flex h-full w-full flex-col px-6 pb-4">
        {isCreateEditCaseSidebarVisible && <CreateEditCaseSidebar closeSidebar={handleCloseCaseSidebarClick} />}

        <div className="flex h-full w-full flex-col overflow-hidden">
          <div className="relative flex h-full w-full flex-row gap-5 overflow-hidden">
            {/* Bordered table card with internal vertical scroll. `px-2`
                gives 8px breathing room between the table content and the
                border. `overflow-x-hidden overflow-y-auto` shows the
                vertical scrollbar only when rows overflow; the horizontal
                bar is suppressed because the JS-driven spacer logic keeps
                the table at 100% width. */}
            <div className="bg-fletchers-white border-fletchers-gray-110 h-full w-full overflow-x-hidden overflow-y-auto rounded-lg border px-2">
              <CaseListTable
                table={table}
                filterOptions={filterOptions}
                selectedFilterOptions={selectedFilterOptions}
                handleFilterChange={handleFilterChange}
                handleTableRowClick={handleTableRowClick}
              />
            </div>

            {isTableDataLoading && (
              <div className="pointer-events-none absolute top-0 left-0 flex h-full w-full items-center justify-center">
                <ProgressSpinner style={{ width: '100px', height: '100px' }} strokeWidth="4" animationDuration="1.5s" />
              </div>
            )}
          </div>
          <div className="mt-auto flex justify-center">
            <TablePagination table={table} appearance={{ dividerClassName: 'hidden' }} />
          </div>
        </div>
      </div>
    </CaseTemplate>
  );
};
